import { useEffect } from 'react';
import { useAuthStore } from '../../context/AuthContext';
import useApiEndpoints from '../../context/useApiEndpoints';
import useApi from '../useApi';
import { mapCargoTemperatureConfigBMs, mapCargoTemperatureConfigs } from '../../factory/CargoTempLog/CargoTempLogConfigFactory';
import { CargoTemperatureConfigBM } from '../../types/BusinessModel/CargoTempLog/CargoTemperatureConfigBM';
import { CargoTemperatureConfig } from '../../types/ViewModel/CargoTempLog/CargoTemperatureConfig';
import { buildQueryParams } from '../endpoints/queryParamsHelper';
import { CargoTemperatureLogEntryBM } from '../../types/BusinessModel/CargoTempLog/CargoTemperatureLogBM';
import { CargoTemperatureInputFieldBM } from '../../types/BusinessModel/CargoTempLog/CargoTemperatureInputFieldBM';
import { CargoTemperatureLogDateBM } from '../../types/BusinessModel/CargoTempLog/CargoTemperatureLogDateBM';
import { mapCargoTempLogDates } from '../../factory/CargoTempLog/CargoTempLogDateFactory';
import { useCargoTempLogStore } from '../../context/CargoTempLogContext';
import { useAolStore } from '../../context/AolContext';
import { mapCargoTemperatureInputFields } from '../../factory/CargoTempLog/CargoTempLogInputFieldFactory';
import { useCargoTempResponseHandler } from '../responseHandlers/useCargoTemperatureResponseHandler';
import useUpdateMissingTasksIcon from '../../app/useUpdateMissingTasksIcon';
import { AppFeatureEnum } from '../../enums/appFeatureEnum';
import useAppFeature from '../../app/useAppFeature';
import apiEndpointUtils from 'src/utils/apiEndpointUtils';
import { getAllCargoTemperatureLogItems, getAllCargoTemperatureConfigsItems } from './offlineCargoTempLogApi';
import { useVesselStore } from 'src/context/VesselContext';

export default () => {
    const { apiEndpoints } = useApiEndpoints();
    const { state, getApiData, postApiData } = useApi();
    const { selectedVessel } = useVesselStore();
    const { cargoTemperatureConfigs, setCargoTemperatureConfigs, setCargoTemperatureInputFields, setCargoTempLogDates } = useCargoTempLogStore();
    const { setSaveAolEvent } = useAolStore();
    const { signalRConnectionId } = useAuthStore();
    const { onSaveCargoTempConfigs, onSaveCargoTempLogs, onGetCargoTempLog } = useCargoTempResponseHandler();
    const { updateCargoTempLogMissingTasks } = useUpdateMissingTasksIcon();
    const { isFeatureEnabled } = useAppFeature();
    const { processApiEndpointError } = apiEndpointUtils();

    const getCargoTemperatureConfigs = (imoNumber: number, cargoNumbers: string, forceRefreshCache?: boolean) =>
        getApiData({
            endpoint: apiEndpoints['getCargoTemperatureConfigs'].url,
            queryParam: buildQueryParams({ imoNumber, cargoNumbers }),
            forceRefreshCache,
        });
    const getCargoTemperatureInputFields = (imoNumber: number, forceRefreshCache?: boolean) =>
        getApiData({
            endpoint: apiEndpoints['getCargoTemperatureInputFields'].url,
            queryParam: buildQueryParams({ imoNumber }),
            forceRefreshCache,
        });

    const getCargoTempLogDates = async (
        imoNumber: number,
        fromDate: string,
        toDate: string,
        stowageIntegrationFromDate: string,
        callInBackground: boolean,
        skipUpdatingContext: boolean = false
    ): Promise<CargoTemperatureLogDateBM[]> => {
        return await getApiData({
            endpoint: apiEndpoints['getCargoTempLogDates'].url,
            queryParam: buildQueryParams({ imoNumber, fromDate, toDate, getCargoInTanksUntilDate: stowageIntegrationFromDate }),
            callInBackground: callInBackground,
            additionalInfo: { skipUpdatingContext: skipUpdatingContext },
        });
    };

    const getCargoTemperatureLog = (imoNumber: number, logDate: string) =>
        getApiData({
            endpoint: apiEndpoints['getCargoTemperatureLog'].url,
            queryParam: buildQueryParams({ imoNumber, logDate }),
        });

    const saveCargoTemperatureConfigs = (imoNumber: number, cargoTempConfigs: CargoTemperatureConfig[]) => {
        postApiData({
            endpoint: apiEndpoints['saveCargoTemperatureConfigs'].url,
            queryParam: buildQueryParams({ imoNumber, connectionId: signalRConnectionId }),
            data: mapCargoTemperatureConfigBMs(cargoTempConfigs),
        });
        setSaveAolEvent(null);
    };

    const saveCargoTemperatureLog = (imoNumber: number, cargoTempLogDate: CargoTemperatureLogDateBM) => {
        postApiData({
            endpoint: apiEndpoints['saveCargoTemperatureLog'].url,
            queryParam: buildQueryParams({ imoNumber, connectionId: signalRConnectionId }),
            data: cargoTempLogDate,
        });
        setSaveAolEvent(null);
    };
    const deleteCargoTemperatureConfigs = (imoNumber: number, ids: number[]) => {
        postApiData({
            endpoint: apiEndpoints['deleteCargoTemperatureConfigs'].url,
            queryParam: buildQueryParams({ imoNumber, connectionId: signalRConnectionId }),
            data: ids,
            additionalInfo: { ids },
        });
        setSaveAolEvent(null);
    };
    useEffect(() => {
        (async () => {
            if (state.status === 'success') {
                if (state.endpoint === apiEndpoints['getCargoTemperatureConfigs'].url) {
                    const bms = state.result as CargoTemperatureConfigBM[];
                    const offlineCargoTemperatureConfigs = await getAllCargoTemperatureConfigsItems(selectedVessel.imoNumber);
                    const config = mapCargoTemperatureConfigs(bms, offlineCargoTemperatureConfigs);
                    setCargoTemperatureConfigs([...config]);
                }
                if (state.endpoint === apiEndpoints['getCargoTemperatureInputFields'].url) {
                    const bms = state.result as CargoTemperatureInputFieldBM[];
                    const fields = mapCargoTemperatureInputFields(bms);
                    setCargoTemperatureInputFields([...fields]);
                }
                if (state.endpoint === apiEndpoints['getCargoTempLogDates'].url) {
                    if (state.additionalInfo?.skipUpdatingContext) return;
                    const bms = state.result as CargoTemperatureLogDateBM[];
                    const offlineCargoTempLogDates = await getAllCargoTemperatureLogItems(selectedVessel.imoNumber);

                    const logDates = mapCargoTempLogDates(bms, offlineCargoTempLogDates);
                    setCargoTempLogDates([...logDates]);
                }
                if (state.endpoint === apiEndpoints['getCargoTemperatureLog'].url) {
                    const bms = state.result as CargoTemperatureLogEntryBM[];
                    onGetCargoTempLog(bms);
                }
                if (state.endpoint === apiEndpoints['saveCargoTemperatureConfigs'].url) {
                    const bms = state.result as CargoTemperatureConfigBM[];
                    onSaveCargoTempConfigs(bms);
                }
                if (state.endpoint === apiEndpoints['saveCargoTemperatureLog'].url) {
                    const bm = state.result as CargoTemperatureLogDateBM;
                    onSaveCargoTempLogs(bm);
                    if (isFeatureEnabled({ featureEnum: AppFeatureEnum.Notifications, date: new Date() })) {
                        updateCargoTempLogMissingTasks(bm.imoNumber);
                    }
                }
                if (state.endpoint === apiEndpoints['deleteCargoTemperatureConfigs'].url) {
                    const ids = state.additionalInfo.ids as number[];
                    if (cargoTemperatureConfigs) {
                        const _cargoTemperatureConfigs = [...cargoTemperatureConfigs].filter(
                            (c) => !ids.some((id) => c.cargoTemperatureConfigId === id)
                        );
                        setCargoTemperatureConfigs([..._cargoTemperatureConfigs]);
                    }
                }
            } else {
                processApiEndpointError(state);
            }
        })();
    }, [state]);

    return {
        state,
        getCargoTemperatureConfigs,
        getCargoTemperatureInputFields,
        getCargoTemperatureLog,
        getCargoTempLogDates,
        saveCargoTemperatureLog,
        saveCargoTemperatureConfigs,
        deleteCargoTemperatureConfigs,
    };
};
